import { CampaignResponseDto } from '../types/campaigns.types';
import {
  defaultWeeklyBonusTiers,
  mapEligibilityRulesToForm,
  normalizeEligibilityProfile,
} from '../constants/campaignScoringDefaults';

export type CampaignEligibilityColumnBlock = 'meta' | 'pontos';

export function resolveCampaignEligibilityProfile(
  campaign: CampaignResponseDto | undefined,
  block: CampaignEligibilityColumnBlock,
) {
  if (!campaign?.eligibilityRules) return null;
  const form = mapEligibilityRulesToForm(campaign.eligibilityRules);
  return block === 'meta' ? form.meta : form.pontos;
}

export function shouldShowRenegColumns(
  campaign: CampaignResponseDto | undefined,
  block: CampaignEligibilityColumnBlock,
): boolean {
  const profile = resolveCampaignEligibilityProfile(campaign, block);
  return profile?.incluirRenegociacao ?? false;
}

/** Colunas "Acessos" refletem linhas aditivas contabilizadas no bloco de elegibilidade. */
export function shouldShowAcessosColumns(
  campaign: CampaignResponseDto | undefined,
  block: CampaignEligibilityColumnBlock,
): boolean {
  const profile = resolveCampaignEligibilityProfile(campaign, block);
  return profile?.incluirAditivo ?? false;
}

/** Perfil de elegibilidade da campanha semanal (fonte meta ou custom). */
export function resolveWeeklyBonusEligibilityProfile(campaign: CampaignResponseDto | undefined) {
  const semanal = campaign?.bonificationRules?.campanhasSemanais;
  const fonte = semanal?.fonteElegibilidade ?? 'meta';
  if (fonte === 'custom') {
    return normalizeEligibilityProfile(semanal?.perfilCustomizado, 'meta');
  }
  return (
    resolveCampaignEligibilityProfile(campaign, 'meta')
    ?? normalizeEligibilityProfile(undefined, 'meta')
  );
}

/** Tipos de linha considerados nos tiers de bonificação da janela semanal. */
export function resolveWeeklyBonusTierLineFlags(
  campaign: CampaignResponseDto | undefined,
  windowIndex: number | null | undefined,
) {
  const semanal = campaign?.bonificationRules?.campanhasSemanais;
  const window = windowIndex != null && windowIndex >= 0
    ? semanal?.windows?.[windowIndex]
    : undefined;
  const tiers = window?.tiersBonificacao?.length
    ? window.tiersBonificacao
    : defaultWeeklyBonusTiers();

  return {
    incluirRenegociacao: tiers.some((t) => Boolean(t.incluirRenegociacao)),
    incluirAditivo: tiers.some((t) => Boolean(t.incluirAditivo)),
  };
}

export function resolveWeeklyBonusLineColumnVisibility(
  campaign: CampaignResponseDto | undefined,
  windowIndex: number | null | undefined,
) {
  const profile = resolveWeeklyBonusEligibilityProfile(campaign);
  const tierFlags = resolveWeeklyBonusTierLineFlags(campaign, windowIndex);

  return {
    showRenegColumns: Boolean(profile.incluirRenegociacao) && tierFlags.incluirRenegociacao,
    showAcessosColumns: Boolean(profile.incluirAditivo) && tierFlags.incluirAditivo,
  };
}

export function resolveCampaignLineColumnVisibility(
  campaign: CampaignResponseDto | undefined,
  options: {
    isCampaignRevenueBucket: boolean;
    isCampaignPointsMode: boolean;
    isCampaignWeeklyBonusMode?: boolean;
    weeklyBonusWindowIndex?: number | null;
  },
) {
  if (options.isCampaignWeeklyBonusMode) {
    return resolveWeeklyBonusLineColumnVisibility(
      campaign,
      options.weeklyBonusWindowIndex,
    );
  }

  if (options.isCampaignPointsMode) {
    return {
      showRenegColumns: shouldShowRenegColumns(campaign, 'pontos'),
      showAcessosColumns: shouldShowAcessosColumns(campaign, 'pontos'),
    };
  }

  if (options.isCampaignRevenueBucket) {
    return {
      showRenegColumns: shouldShowRenegColumns(campaign, 'meta'),
      showAcessosColumns: shouldShowAcessosColumns(campaign, 'meta'),
    };
  }

  return {
    showRenegColumns: true,
    showAcessosColumns: true,
  };
}
